import React, { useCallback, useMemo } from 'react'
import { Trans, useLingui } from '@lingui/react/macro'
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  ConnectionHandler,
} from 'react-relay'
import * as Yup from 'yup'
import { useNavigate } from 'react-router-dom'
import { addDays, addHours } from 'date-fns'
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'
import { FormikConfig, FormikContextType, useFormikContext } from 'formik'

import { EventFormDatePicker } from '@newComponents/Event/EventFormDatePicker'

import { XStack, YStack } from 'tamagui'
import { H2, Paragraph, Text } from '@newComponents/Text'
import { EmojiIcon } from '@newComponents/EmojiIcon/EmojiIcon'
import { FormTimezonePicker } from '@newComponents/Form/FormTimezonePicker'

import { Form } from '@newComponents/Form/Form'
import { FormField } from '@newComponents/Form/FormField'
import { FormTextInput } from '@newComponents/Form/FormTextInput'
import { FormSubmitButton } from '@newComponents/Form/FormSubmitButton'
import { FormSelect } from '@newComponents/Form/FormSelect'

import { DEFAULT_TIMEZONE } from '@data/timezones'
import { INCLUDE_IN_EVENTS_FEED } from '@data/config/meetup'
import { useMutation } from '@data/useMutation'
import { useFlashMessage } from '@newComponents/DynamicIsland/useFlashMessage'

import { EventNewContentQuery } from './__generated__/EventNewContentQuery.graphql'
import { EventNewContentCreateEventMutation } from './__generated__/EventNewContentCreateEventMutation.graphql'
import { EventNewContent_user$key } from './__generated__/EventNewContent_user.graphql'

type FormValues = {
  endAt: Date
  name: string
  ownerId: string
  startAt: Date
  timeZone: string
}
type EventOwnerSelectProps = {
  createOnValueChangeHandler: (
    formikContext: FormikContextType<FormValues>
  ) => (ownerId: string) => void
  items: Array<{ value: string; text: string }>
}

const useValidationSchema = () => {
  const { t } = useLingui()
  return useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string().required(t`Required`),
        timeZone: Yup.string().required(t`Required`),
        startAt: Yup.date()
          .typeError(t`Invalid Date`)
          .required(t`Required`),
        endAt: Yup.date()
          .typeError(t`Invalid Date`)
          .required(t`Required`)
          .when('startAt', (startAt: string, schema: Yup.DateSchema) => {
            if (schema.isValidSync(startAt)) {
              return schema.min(startAt, t`is before Start`)
            } else {
              return schema
            }
          }),
        ownerId: Yup.string()
          .uuid()
          .min(1)
          .required(t`Required`),
      }),
    [t]
  )
}

/**
 * Thin wrapper around FormSelect to allow us to use formikContext in createOnValueChangeHandler
 */
const EventOwnerSelect = (props: EventOwnerSelectProps) => {
  const formikContext = useFormikContext<FormValues>()

  const handleOwnerIdChange = props.createOnValueChangeHandler(formikContext)

  return <FormSelect items={props.items} onValueChange={handleOwnerIdChange} />
}

export const EventNewContent = () => {
  const { t } = useLingui()
  const validationSchema = useValidationSchema()

  const query = useLazyLoadQuery<EventNewContentQuery>(
    graphql`
      query EventNewContentQuery {
        viewer {
          ...EventNewContent_user
        }
      }
    `,
    {}
  )

  const fragment = useFragment<EventNewContent_user$key>(
    graphql`
      fragment EventNewContent_user on User {
        rowId
        slugId
        firstName
        lastName

        organizingGuilds: guildMemberships(
          first: 10
          condition: { membershipLevel: ORGANIZER }
        ) {
          nodes {
            guild {
              defaultTimeZone
              rowId
              name
            }
          }
        }

        editingGuilds: guildMemberships(
          first: 10
          condition: { membershipLevel: EDITOR }
        ) {
          nodes {
            guild {
              defaultTimeZone
              rowId
              name
            }
          }
        }
      }
    `,
    query.viewer
  )

  const createEventMutation = useMutation<EventNewContentCreateEventMutation>(
    graphql`
      mutation EventNewContentCreateEventMutation(
        $input: CreateEventWithSlugMutationInput!
      ) {
        createEventWithSlug(input: $input) {
          event {
            prettyUrl

            owner {
              ... on Guild {
                ...GuildEventsPastCard_guild
                ...GuildEventsUpcomingCard_guild
              }
            }
          }

          query {
            ...EventsScreenList_query
          }
        }
      }
    `
  )

  const navigate = useNavigate()
  const { showFlash } = useFlashMessage()

  const guildNodes = useMemo(
    () => [
      ...(fragment?.organizingGuilds?.nodes ?? []),
      ...(fragment?.editingGuilds?.nodes ?? []),
    ],
    [fragment]
  )

  const initialValues = useMemo(() => {
    const initialGuildNode = guildNodes.find((node) => node?.guild?.rowId)
    const ownerId = initialGuildNode?.guild?.rowId ?? fragment?.rowId ?? ''
    const timeZone =
      initialGuildNode?.guild?.defaultTimeZone ?? DEFAULT_TIMEZONE

    const nowBareHour =
      timeZone === DEFAULT_TIMEZONE
        ? new Date().setMinutes(0, 0, 0)
        : utcToZonedTime(
            zonedTimeToUtc(new Date().setMinutes(0, 0, 0), DEFAULT_TIMEZONE),
            timeZone
          )

    const startAt = addHours(addDays(nowBareHour, 1), 1)
    const endAt = addHours(addDays(nowBareHour, 1), 3)

    return {
      name: '',
      timeZone,
      startAt,
      endAt,
      ownerId,
    }
  }, [fragment, guildNodes])

  const handleSubmit: FormikConfig<FormValues>['onSubmit'] = useCallback(
    async (values) => {
      const ownerType = values.ownerId === fragment?.rowId ? 'users' : 'guilds'
      const response = await createEventMutation({
        variables: {
          input: {
            ownerType,
            ...values,
            startAt: zonedTimeToUtc(
              values.startAt,
              values.timeZone
            ).toISOString(),
            endAt: zonedTimeToUtc(values.endAt, values.timeZone).toISOString(),
          },
        },
        updater: (store) => {
          const root = store.getRoot()

          const connections = [
            ConnectionHandler.getConnection(
              root,
              'EventsScreenList_query_upcomingEvents',
              {
                timeSelection: 'UPCOMING',
                ...(INCLUDE_IN_EVENTS_FEED
                  ? {
                      filter: { allowGlobalListing: { equalTo: true } },
                    }
                  : {
                      filter: {
                        and: [
                          {
                            not: {
                              metadata: {
                                containsKey: 'meetupEventID',
                              },
                            },
                          },
                          { allowGlobalListing: { equalTo: true } },
                        ],
                      },
                    }),
              }
            ),
          ]

          if (ownerType === 'users') {
            const userRecord = store.get(
              btoa(JSON.stringify(['users', fragment?.rowId]))
            )

            if (userRecord) {
              connections.push(
                ConnectionHandler.getConnection(
                  userRecord,
                  'UserEventsUpcomingSection_user_upcomingEvents',
                  { timeSelection: 'UPCOMING' }
                ),
                ConnectionHandler.getConnection(
                  userRecord,
                  'UserEventsPastSection_user_pastEvents',
                  { timeSelection: 'PAST' }
                )
              )
            }
          }

          if (ownerType === 'guilds') {
            const guildRecord = store.get(
              btoa(JSON.stringify(['guilds', values.ownerId]))
            )

            if (guildRecord) {
              connections.push(
                ConnectionHandler.getConnection(
                  guildRecord,
                  'GuildEventsPastCard_guild_pastEvents',
                  {
                    timeSelection: 'PAST',
                    orderBy: ['START_AT_DESC', 'ID_ASC'],
                  }
                ),

                ConnectionHandler.getConnection(
                  guildRecord,
                  'GuildEventsUpcomingCard_guild_upcomingEvents',
                  {
                    timeSelection: 'UPCOMING',
                    orderBy: ['START_AT_ASC', 'ID_ASC'],
                  }
                )
              )
            }
          }

          connections.map((connection) => connection?.invalidateRecord())
        },
      })

      const event = response.createEventWithSlug.event

      if (event) {
        showFlash(t`Event created!`, 'success')
        navigate(`/events/${event.prettyUrl}/settings`)
      } else {
        showFlash(t`Failed to create Event`, 'error')
        console.error('SERVER SIDE ERROR FROM EVENT CREATION', response)
      }
    },
    [createEventMutation, fragment?.rowId, navigate, showFlash]
  )

  const createOnValueChangeHandler = useCallback(
    (formikContext: FormikContextType<FormValues>) => (ownerId: string) => {
      const guildNode = guildNodes.find(
        (guildNode) => ownerId === guildNode?.guild?.rowId
      )
      const defaultTimeZone =
        guildNode?.guild?.defaultTimeZone || DEFAULT_TIMEZONE

      if (formikContext.values.timeZone === defaultTimeZone) {
        return
      }

      void formikContext.setFieldValue('timeZone', defaultTimeZone, false)
    },
    [guildNodes]
  )

  const ownerItems = useMemo(() => {
    const items: Array<{ value: string; text: string }> = []

    fragment?.organizingGuilds?.nodes.forEach((node) => {
      if (node?.guild?.rowId && node?.guild?.name) {
        items.push({ value: node.guild.rowId, text: node.guild.name })
      }
    })

    fragment?.editingGuilds?.nodes.forEach((node) => {
      if (node?.guild?.rowId && node?.guild?.name) {
        items.push({ value: node.guild.rowId, text: node.guild.name })
      }
    })

    if (fragment?.rowId) {
      items.push({
        value: fragment.rowId,
        text: t`No Guild, create an Event for myself`,
      })
    }

    return items
  }, [fragment])

  if (!query?.viewer) {
    // TODO error, can't find who's creating the event
    return null
  }

  return (
    <YStack gap='$6'>
      <XStack gap='$4' alignItems='center'>
        <EmojiIcon name='spiral-calendar' size='$9' />
        <YStack gap='$1' flex={1}>
          <Text size='$7' fontWeight='$7'>
            <Trans>Create an Event</Trans>
          </Text>
          <Paragraph size='$4' color='$colorMuted'>
            <Trans>Set up a new Event for your community</Trans>
          </Paragraph>
        </YStack>
      </XStack>

      <Form
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <YStack gap='$5'>
          <FormField name='name' label={t`Title`}>
            <FormTextInput placeholder={t`Your event title`} />
          </FormField>

          <FormField name='ownerId' label={t`For a Guild or myself`}>
            <EventOwnerSelect
              createOnValueChangeHandler={createOnValueChangeHandler}
              items={ownerItems}
            />
          </FormField>

          <YStack gap='$2'>
            <H2 fontWeight='$5'>
              <Trans>When and how long?</Trans>
            </H2>

            <YStack gap='$4'>
              <EventFormDatePicker />
            </YStack>
          </YStack>

          <FormField name='timeZone' label={t`Time Zone`}>
            <FormTimezonePicker />
          </FormField>

          <XStack gap='$4' alignItems='center'>
            <XStack flex={1}>
              <Paragraph color='$color' fontWeight='$3' size='$4'>
                <Trans>You can add more details later</Trans>
              </Paragraph>
            </XStack>

            <FormSubmitButton>
              <FormSubmitButton.Text>
                <Trans>Continue</Trans>
              </FormSubmitButton.Text>
            </FormSubmitButton>
          </XStack>
        </YStack>
      </Form>
    </YStack>
  )
}

export default EventNewContent
